import { AfterViewInit, Component, Input, NgZone, OnInit, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import { InvestmentModel } from '../../../../core/models/investment/investment.model';
import { DefaultProfileImage } from '../../../../core/services/constant';
import { ReviewStatus } from '../../../../core/enum/share-review-status.enum';
import { PaymentTo } from '../../../../core/enum/payment-to-type.enum';
import { ShareType } from '../../../../core/enum/share-type.enum';
import { CampaignService } from '../../../../core/services/api/campaign.service';
import { ErrorService } from '../../../../core/services/error.service';
import { ToastService } from '../../../../core/services/toast.service';
import { TranslateService } from '@ngx-translate/core';
import { BlockUiService } from '../../../../shared/services/blockUi/block-ui.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { catchError, finalize, take } from 'rxjs/operators';
import { of, throwError } from 'rxjs';
@Component({
    selector: 'app-share-review-edit-popup',
    templateUrl: './share-review-edit-popup.component.html',
    styleUrls: ['./share-review-edit-popup.component.scss'],
    standalone: false
})
export class ShareReviewEditPopupComponent implements OnInit, AfterViewInit {
  @Input() investment: InvestmentModel | null = null;
  @Input() campaignId: number | null = null;
  @Input() shareId: number | null = null;
  @Input() isEditMode: boolean = false;
  readonly maxRemarkLength = 250;
  remark = '';
  submitting: boolean = false;
  isRemarkEditable: boolean = true;
  showRequestDetails: boolean = true;
  reviewSubmittedOn: string | null = null;
  existingRequestExists: boolean | null = null;
  @ViewChild('remarkAutosize') remarkAutosize?: CdkTextareaAutosize;

  constructor(
    public activeModal: NgbActiveModal,
    private campaignService: CampaignService,
    private errorService: ErrorService,
    private blockUiService: BlockUiService,
    private snackBar: MatSnackBar,
    private toastService: ToastService,
    private translate: TranslateService,
    private ngZone: NgZone,
  ) {}
  ReviewStatus = ReviewStatus;
  defaultProfileImage = DefaultProfileImage;
  PaymentTo = PaymentTo;
  ShareType = ShareType;

  ngOnInit(): void {
    this.isRemarkEditable = !this.isLockedStatus;
    this.showRequestDetails = !this.isRevised;
    this.loadExistingReview();
  }

  ngAfterViewInit(): void {
    this.triggerRemarkResize();
  }

  close(): void {
    this.activeModal.dismiss('close');
  }

  cancel(): void {
    this.activeModal.dismiss('cancel');
  }

  submitRequest(form: NgForm): void {
    form.control.markAllAsTouched();
    const remark = (this.remark?.trim() ?? '').slice(0, this.maxRemarkLength);
    this.remark = remark;
    if (!remark) return;
    const campaignId = this.campaignId;
    const shareId = this.shareId;
    if (!campaignId || !shareId) {
      this.snackBar.open('Unable to submit: missing campaign/share id.', '', { duration: 2500 });
      return;
    }
    if (this.submitting) return;

    this.submitting = true;
    this.blockUiService.open();

    const create$ = this.campaignService.createShareReviewRequest(campaignId, shareId, remark);
    const update$ = this.campaignService.updateShareReviewRequest(campaignId, shareId, remark);
    let didUpdate = this.isEditMode;

    (this.isEditMode ? update$ : create$)
      .pipe(
        catchError((err) => {
          const errorCode =
            err?.error?.code ??
            err?.Error?.Code ??
            err?.Error?.code ??
            err?.code ??
            err?.ErrorCode ??
            '';

          if (!this.isEditMode && errorCode === 'IRR_EXISTS') {
            didUpdate = true;
            return update$;
          }
          if (!this.isEditMode && errorCode === 'IRR_REQUIRED') {
            this.snackBar.open('Remark is required.', '', { duration: 2000 });
            return of(null);
          }

          if (this.isEditMode && errorCode === 'IRR_NOT_FOUND') {
            didUpdate = false;
            return create$;
          }
          return throwError(() => err);
        }),
        finalize(() => {
          this.submitting = false;
          this.blockUiService.close();
        }),
      )
      .subscribe({
        next: (res) => {
          if (res === null) return;
          this.toastService.success(
            didUpdate ? 'Revision request updated' : 'Revision request submitted',
          );
          this.activeModal.close(true);
        },
        error: (err) => {
          this.errorService.showError(err);
        },
      });
  }

  private loadExistingReview(): void {
    const campaignId = this.campaignId;
    const shareId = this.shareId;
    if (!campaignId || !shareId) return;

    this.campaignService.getShareReviewRequest(campaignId, shareId).subscribe({
      next: (res) => {
        const exists = !!res?.exists;
        this.existingRequestExists = exists;
        this.isEditMode = exists;
        this.reviewSubmittedOn = res?.modifyAt ?? null;
        this.remark = ((exists ? res?.reviewMessage : '') ?? '')
          .toString()
          .slice(0, this.maxRemarkLength);
        this.triggerRemarkResize();
      },
      error: (err) => {
        this.errorService.showError(err);
      },
    });
  }

  onRemarkChange(value: string): void {
    this.remark = (value ?? '').toString().slice(0, this.maxRemarkLength);
    this.triggerRemarkResize();
  }

  get isAwaitingRevision(): boolean {
    return this.investment?.reviewStatus === ReviewStatus.AwaitingRevision;
  }

  get isRevised(): boolean {
    return this.investment?.reviewStatus === ReviewStatus.Revised;
  }

  get isApproved(): boolean {
    return this.investment?.reviewStatus === ReviewStatus.Approved;
  }

  get isLockedStatus(): boolean {
    return this.isAwaitingRevision || this.isApproved;
  }

  get isRemarkReadOnly(): boolean {
    return this.isLockedStatus && !this.isRemarkEditable;
  }

  get isRemarkDisabled(): boolean {
    return this.submitting || this.isRemarkReadOnly;
  }

  get remarkMinRows(): number {
    return this.isRemarkReadOnly ? 1 : 4;
  }

  enableRemarkEditing(): void {
    this.isRemarkEditable = true;
    this.triggerRemarkResize();
  }

  disableRemarkEditing(): void {
    if (!this.isLockedStatus) return;
    this.isRemarkEditable = false;
    this.triggerRemarkResize();
  }

  openRequestEditInfo(): void {
    this.showRequestDetails = true;
    this.isRemarkEditable = true;
    this.triggerRemarkResize();
  }

  private triggerRemarkResize(): void {
    this.ngZone.onStable.pipe(take(1)).subscribe(() => {
      this.remarkAutosize?.resizeToFitContent(true);
    });
  }

  approve(): void {
    const campaignId = this.campaignId;
    const shareId = this.shareId;
    if (!campaignId || !shareId) {
      this.snackBar.open('Unable to approve: missing campaign/share id.', '', { duration: 2500 });
      return;
    }
    if (this.submitting) return;

    if (this.investment?.reviewStatus === ReviewStatus.Approved) {
      this.toastService.success(this.translate.instant('toast.investorApproved'));
      return;
    }

    this.submitting = true;
    this.blockUiService.open();

    this.campaignService
      .approveShareReview(campaignId, shareId)
      .pipe(
        finalize(() => {
          this.submitting = false;
          this.blockUiService.close();
        }),
      )
      .subscribe({
        next: () => {
          if (this.investment) this.investment.reviewStatus = ReviewStatus.Approved;
          this.toastService.success(this.translate.instant('toast.investorApproved'));
          this.activeModal.close(true);
        },
        error: (err) => {
          this.errorService.showError(err);
        },
      });
  }

  OnChangeformatInvestorName(fullName?: string | null): string {
    if (!fullName) return '';

    const normalized = fullName.trim().replace(/\s+/g, ' ');
    const words = normalized.split(' ').map((word) => {
      const lower = word.toLowerCase();
      if (lower === 'a/l') return 'A/L';
      if (lower === 'a/p') return 'A/P';
      return lower.replace(/(^[a-z])|([-'][a-z])/g, (m) => m.toUpperCase());
    });

    return words
      .join(' ')
      .replace(/(^|\s)a\/l(?=\s|$|[.,;:)\]])/gi, '$1A/L')
      .replace(/(^|\s)a\/p(?=\s|$|[.,;:)\]])/gi, '$1A/P');
  }
  getReviewStatusClass(status: number): string {
    switch (status) {
      case 1:
        return 'pendingReviewstatus';
      case 2:
        return 'paidstatus';
      case 3:
        return 'completedstatus';
      case 4:
        return 'awaitingRevisionstatus';
      case 5:
        return 'requestrefundstatus';
      default:
        return '';
    }
  }
}
